import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setCurrentView } from '../../../../redux/sequence/sequence-slice';
import { Sequence, Step } from '../../../../redux/sequence/types';
import { getText } from '../../../../utils/getter';
import { isAdmin } from '../../../../utils/helper';
import { ReactComponent as Check } from '../icons/check.svg';
import './progress-bar.css';

interface ProgressBarProps extends React.HTMLProps<HTMLDivElement> {
  sequence: Sequence;
  steps?: Step[];
}

const ProgressBar: React.FC<ProgressBarProps> = ({ sequence, steps }) => {
  // Dispatch hook
  const dispatch = useDispatch();

  // Navigation hook
  const navigate = useNavigate();

  // Step storage
  const { step, completedTillStep } = sequence;

  // Application ID
  const leaseApplicationID = window.location.pathname.split('/')[2];

  // Current and next step
  const currentStep = steps?.[step];
  const nextStep = steps?.[step + 1];

  // Navigate to a given step
  const goToStep = (index: number) => {
    if (!steps) return;
    dispatch(setCurrentView({ ...sequence, step: index }));
    navigate(
      steps[index].route(
        leaseApplicationID || '',
        (sequence?.sequence + 1).toString(),
      ),
    );
  };

  return (
    <div className='progress' data-testid='progress-bar'>
      {currentStep && <p className='progress-title'>{currentStep.name}</p>}
      <div className='progress-steps'>
        {steps?.map((value, index) => {
          const isSelectable =
            step >= index ||
            import.meta.env.MODE === 'development' ||
            isAdmin();
          const isActive = step === index;
          const isCompleted = step > index;
          return (
            <React.Fragment key={value.key}>
              {index > 0 && <div className='progress-connector' />}
              <div
                className={`progress-step-circle focus-style ${
                  isActive ? 'active' : ''
                } ${isCompleted ? 'completed' : ''} ${
                  completedTillStep >= index ? 'clickable' : ''
                }`}
                role='button'
                aria-label={value.name}
                aria-current={isActive ? 'step' : undefined}
                tabIndex={isSelectable ? 0 : -1}
                onKeyDown={(e) => {
                  if (!isSelectable) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToStep(index);
                  }
                }}
                onClick={() => {
                  if (isSelectable) goToStep(index);
                }}
              >
                {isCompleted ? <Check aria-hidden='true' /> : index + 1}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {nextStep && (
        <div className='progress-next-up'>
          <span className='progress-next-label'>
            {getText('next_step_label', 'Nächster Schritt')}
          </span>
          <span className='progress-next-title'>{nextStep.name}</span>
        </div>
      )}
    </div>
  );
};

export default ProgressBar;

